import { useState } from 'react'
import BigButton from './BigButton'
import { api } from '../lib/api'
import { useSeason } from '../lib/season'
import type { AdminRegistration } from '../lib/types'

// Kant-en-klare feedbackvraag, zodat de organisator na de markt niet zelf een
// tekst hoeft te verzinnen. De persoonlijke link eronder leidt naar het
// feedbackformulier op /mijn-plek.
const FEEDBACK_PRESET = {
  subject: 'Hoe was de rommelmarkt voor jou?',
  body:
    'Hallo {voornaam},\n\n' +
    'De rommelmarkt zit erop — bedankt dat je erbij was!\n\n' +
    'Eén korte vraag nog: hoe was het voor jou? Via de knop hieronder kom je op je eigen ' +
    'pagina, met drie korte vraagjes. Tikken volstaat, typen hoeft niet.\n\n' +
    'Je vindt daar ook je inschrijving van deze editie terug.\n\n' +
    'Dankjewel!\n' +
    'Het rommelmarkt-team',
}

type Props = {
  recipients: AdminRegistration[]
  token: string
  onClose: () => void
  onSent: (sent: number, failed: { naam: string; reason: string }[]) => void
}

export default function BroadcastModal({ recipients, token, onClose, onSent }: Props) {
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('Hallo {voornaam},\n\n')
  const [includeLink, setIncludeLink] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { season } = useSeason()
  const naMarkt = season !== null && !season.registrationOpen

  const recipientPreview = recipients.length > 3
    ? `${recipients.slice(0, 3).map((r) => r.naam).join(', ')}, +${recipients.length - 3} meer`
    : recipients.map((r) => r.naam).join(', ')

  async function send() {
    setError(null)
    if (subject.trim().length < 2) { setError('Vul een onderwerp in.'); return }
    if (body.trim().length < 2) { setError('Vul een bericht in.'); return }
    if (recipients.length >= 2) {
      if (!window.confirm(`${recipients.length} mails versturen?`)) return
    }
    setBusy(true)
    const res = await api<{ sent: number; failed: { naam: string; reason: string }[] }>(
      '/api/admin/email',
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: JSON.stringify({ subject, body, includeLink, recipientPageIds: recipients.map((r) => r.id) }),
      },
    )
    setBusy(false)
    if (!res.ok) {
      if (res.errors) setError(Object.values(res.errors).join(' · '))
      else setError('Versturen mislukt.')
      return
    }
    onSent(res.data.sent, res.data.failed)
  }

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-offwhite border-[3px] border-ink p-5 max-w-2xl w-full max-h-[90vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-baseline justify-between mb-3">
          <h2 className="font-display text-2xl uppercase tracking-display">
            Bericht aan {recipients.length} {recipients.length === 1 ? 'persoon' : 'personen'}
          </h2>
          <button onClick={onClose} className="text-sm text-muted underline">Sluit</button>
        </div>

        <div className="text-sm mb-3">
          <strong>Ontvangers:</strong> {recipientPreview || '(geen)'}
        </div>

        {naMarkt && (
          <button
            type="button"
            onClick={() => { setSubject(FEEDBACK_PRESET.subject); setBody(FEEDBACK_PRESET.body); setIncludeLink(true) }}
            className="mb-3 px-3 py-1.5 text-sm bg-lime border-[3px] border-ink shadow-block-sm font-display tracking-display uppercase hover:translate-y-[1px]"
          >
            ✎ Feedback vragen (klaar bericht)
          </button>
        )}

        <input
          className="input mb-3"
          placeholder="Onderwerp"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
        />

        <textarea
          className="input min-h-[12rem] mb-2 font-mono text-sm"
          placeholder="Bericht…"
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />

        <p className="text-xs text-muted mb-2">
          ⓘ <code>{'{voornaam}'}</code> wordt vervangen door de echte voornaam per ontvanger.
        </p>

        <label className="flex items-start gap-2 mb-3 text-sm cursor-pointer">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={includeLink}
            onChange={(e) => setIncludeLink(e.target.checked)}
          />
          <span>
            Voeg onderaan de <strong>persoonlijke plek-link</strong> toe (knop "Bekijk of kies je plek").
            Elke ontvanger krijgt zo z'n eigen link naar <code>/mijn-plek</code>.
          </span>
        </label>

        {error && (
          <div className="border-[3px] border-red bg-red/10 px-4 py-2 mb-3 text-sm text-red font-semibold">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-3">
          <button type="button" onClick={onClose} className="text-sm underline text-muted self-center">
            Annuleer
          </button>
          <BigButton onClick={send} disabled={busy || recipients.length === 0}>
            {busy ? 'Versturen…' : `Verstuur (${recipients.length})`}
          </BigButton>
        </div>
      </div>
    </div>
  )
}
